import React, { Component, ErrorInfo, ReactNode } from 'react';
import { isSupabaseInitialized } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary specifically for catching Supabase initialization issues
 * Provides helpful guidance to users when database connection fails
 */
class SupabaseErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Supabase Error Boundary caught an error:', error);
    console.error('Component stack:', errorInfo.componentStack);
  }

  handleRetry = () => {
    // Reload the page to attempt initialization again
    window.location.reload();
  };

  handleClearCache = () => {
    // Clear localStorage and sessionStorage before reloading
    try {
      localStorage.clear();
      sessionStorage.clear();
      console.log('Browser storage cleared');
    } catch (e) {
      console.error('Failed to clear storage:', e);
    }
    
    window.location.reload();
  };

  render() {
    // Check if there's an error and if it might be related to Supabase
    if (this.state.hasError) {
      const isSupabaseError = !isSupabaseInitialized() || 
        (this.state.error?.message && (
          this.state.error.message.includes('Supabase') ||
          this.state.error.message.includes('database') ||
          this.state.error.message.includes('connection')
        ));
      
      if (isSupabaseError) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
            <div className="bg-white rounded-lg shadow-md p-6 max-w-md w-full">
              <div className="flex items-center text-amber-500 mb-4">
                <AlertTriangle className="h-6 w-6 mr-2" />
                <h2 className="text-xl font-semibold">Database Connection Issue</h2>
              </div>
              
              <p className="text-gray-700 mb-4">
                We're having trouble connecting to our database services. This could be due to:
              </p>
              
              <ul className="list-disc pl-5 mb-6 text-gray-600 space-y-1">
                <li>Temporary network issues</li>
                <li>Browser storage conflicts</li>
                <li>Service maintenance</li>
              </ul>
              
              <div className="space-y-3">
                <Button 
                  onClick={this.handleRetry} 
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  Reload Page
                </Button>
                
                <Button 
                  onClick={this.handleClearCache} 
                  variant="outline" 
                  className="w-full"
                >
                  Clear Cache & Reload
                </Button>
              </div>
              
              <p className="text-xs text-gray-500 mt-4">
                Error details: {this.state.error?.message || 'Unknown error'}
              </p>
            </div>
          </div>
        );
      }
      
      // Use custom fallback if provided
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }
      
      // Default error UI for non-Supabase errors
      return (
        <div className="p-4 border border-red-300 bg-red-50 rounded-md">
          <h3 className="text-red-700 font-medium">Something went wrong</h3>
          <p className="text-sm text-red-600">
            {this.state.error?.message || 'An unexpected error occurred'}
          </p>
          <Button 
            onClick={this.handleRetry} 
            variant="outline" 
            size="sm"
            className="mt-2"
          >
            Try Again
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default SupabaseErrorBoundary; 